import React, {
  createContext,
  type PropsWithChildren,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react'
import classnames from 'classnames'

import {
  setEditingFeed,
  useIsEditingAnyMix,
} from '../../../../firebase/hooks/useMixes'
import useFeatures from '../../../../sharedLegacy/hooks/useFeatures'
import {
  COVER_ZOOM,
  type MediaType,
  posToStyle,
  updateFeedPosition,
  useFeed,
} from '../../../layouts'

import './TrackPositioner.scss'

type Point = { x: number; y: number }

interface TrackPositionerProps {
  trackId: number
  trackType: MediaType
}

const diffPoints = (pointA: Point, pointB: Point) => {
  return {
    x: pointA.x - pointB.x,
    y: pointA.y - pointB.y,
  }
}

const attachMouse = (callback: (event: MouseEvent) => void) => {
  let queuedUpdate: (() => void) | null = null

  const queueUpdate = (event: MouseEvent) => {
    const newUpdate = () => {
      callback(event)
      queuedUpdate = null
    }
    if (!queuedUpdate) {
      queuedUpdate = newUpdate
      requestAnimationFrame(() => queuedUpdate?.())
    } else {
      queuedUpdate = newUpdate
    }
  }

  window.addEventListener('mousemove', queueUpdate)

  window.addEventListener(
    'mouseup',
    () => {
      window.removeEventListener('mousemove', queueUpdate)
    },
    { once: true }
  )
}

const TrackPositioner = ({
  trackId,
  trackType,
  children,
}: PropsWithChildren<TrackPositionerProps>) => {
  const { trackSize } = useTrackContext()
  const feed = useFeed(trackType, trackId)
  const isEditingAnyMix = useIsEditingAnyMix()
  const { HasWebinarMixesEnabled } = useFeatures()
  const style = posToStyle(feed, { withAnchor: true })

  const geometry = useMemo(() => {
    // TODO: improve defaults
    const {
      // eslint-disable-next-line @typescript-eslint/no-useless-default-assignment
      position: { w, h } = { w: 33, h: 25 },
      // eslint-disable-next-line @typescript-eslint/no-useless-default-assignment
      crop: { zoom } = { zoom: 0 },
    } = feed ?? { position: { w: 33, h: 25 }, crop: { zoom: 0 } }

    // aspect ratio of the box (converted from
    const aspectRatio = ((w / h) * 16) / 9
    // aspect ratio of the video
    const innerRatio = trackSize.width / trackSize.height
    // how much wider than the video is the box
    const ratioComp = aspectRatio / innerRatio

    const isWide = ratioComp >= 1
    // how much wider or taller than the video is the box; essentially the cover/contain ratio
    const absRatio = isWide ? ratioComp : 1 / ratioComp
    // const absRatio = Math.exp(Math.abs(Math.log(ratioComp))) // — alternative calculation

    // [0-1] how much to zoom out the video. equivalent to [cover - contain]
    const zoomOut = zoom < COVER_ZOOM ? 1 - zoom / COVER_ZOOM : 0
    // [0-1] how much to zoom in the video. equivalent to [cover - 4x]
    const zoomIn =
      zoom > COVER_ZOOM ? (zoom - COVER_ZOOM) / (100 - COVER_ZOOM) : 0

    // how much to scale the video based on zoom values. [contain - cover - 4x]
    const scale = 1 + (1 - zoomOut) * (absRatio - 1) + zoomIn * (4 - absRatio)

    // how much we're scaling in relation to covering the box
    const scaleRatio = scale / absRatio

    // how much bigger than the box is the video getting scaled in the long axis
    const lScale =
      absRatio +
      zoomOut * (1 - absRatio) +
      zoomIn * (scaleRatio / ratioComp - absRatio)

    // how much bigger than the box is the video getting scaled in the short axis
    const sScale = 1 + zoomOut * (ratioComp - 1) + zoomIn * (scaleRatio - 1)

    const lLimit = (lScale - 1) / 2
    const sLimit = (sScale - 1) / 2

    const xScale = isWide ? sScale : lScale
    const xLimit = isWide ? sLimit : lLimit
    const yScale = isWide ? lScale : sScale
    const yLimit = isWide ? lLimit : sLimit

    return { scale, xScale, yScale, xLimit, yLimit }
  }, [feed, trackSize.height, trackSize.width])

  const attachAndMove = useCallback(
    (cEvent: React.MouseEvent<HTMLDivElement>) => {
      cEvent.nativeEvent.stopPropagation()
      // @ts-expect-error — TODO: better types
      setEditingFeed(feed.index)

      const track = cEvent.currentTarget
      const rectSize = { w: track.clientWidth, h: track.clientHeight }
      const startPoint = { x: cEvent.clientX, y: cEvent.clientY }
      let startPosition: Point | undefined

      attachMouse((event) => {
        const { x, y } = diffPoints({ x: event.x, y: event.y }, startPoint)
        updateFeedPosition((f) => {
          if (!startPosition)
            startPosition = { x: f.crop.centerX, y: f.crop.centerY }

          const xOff = x / (geometry.xLimit * rectSize.w)
          const yOff = y / (geometry.yLimit * rectSize.h)

          const newPosition = {
            x: Math.min(1, Math.max(-1, startPosition.x + xOff)),
            y: Math.min(1, Math.max(-1, startPosition.y + yOff)),
          }

          if (Math.abs(geometry.xLimit) <= 0.001) newPosition.x = 0
          if (Math.abs(geometry.yLimit) <= 0.001) newPosition.y = 0

          return {
            ...f,
            crop: {
              zoom: f.crop.zoom,
              centerX: newPosition.x,
              centerY: newPosition.y,
            },
          }
        })
      })
    },
    [feed?.index, geometry.xLimit, geometry.yLimit]
  )

  return (
    // eslint-disable-next-line jsx-a11y/click-events-have-key-events,jsx-a11y/no-static-element-interactions
    <div
      data-id={trackId}
      data-x-limit={geometry.xLimit}
      data-y-limit={geometry.yLimit}
      className={classnames('track-positioner', `${trackType}-track`)}
      style={{
        ...style,
        '--scale': geometry.scale,
        '--x-offset': geometry.xLimit * (feed?.crop.centerX ?? 0),
        '--y-offset': geometry.yLimit * (feed?.crop.centerY ?? 0),
      }}
      onClick={
        HasWebinarMixesEnabled
          ? () => {
              if (isEditingAnyMix && feed) setEditingFeed(feed.index)
            }
          : undefined
      }
      onMouseDown={
        HasWebinarMixesEnabled && isEditingAnyMix ? attachAndMove : undefined
      }
    >
      <div className="track-wrapper">{children}</div>
    </div>
  )
}

const DEFAULT_TRACK_CONTEXT_VALUE = { width: 1920, height: 1080 }

type TrackSize = { width: number; height: number }
type TrackContextType = {
  trackSize: TrackSize
  setTrackSize: (trackSize: TrackSize) => void
}

const TrackContext = createContext<TrackContextType>({
  trackSize: DEFAULT_TRACK_CONTEXT_VALUE,
  setTrackSize: () => {},
})

const useTrackContext = () => {
  const context = useContext(TrackContext)

  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  return context ?? DEFAULT_TRACK_CONTEXT_VALUE
}

const TrackPositionerWithContext = ({
  trackId,
  trackType,
  children,
}: PropsWithChildren<TrackPositionerProps>) => {
  const [trackSize, setTrackSize] = useState(DEFAULT_TRACK_CONTEXT_VALUE)

  const value = useMemo(() => {
    return { trackSize, setTrackSize }
  }, [trackSize])

  return (
    <TrackContext.Provider value={value}>
      <TrackPositioner trackId={trackId} trackType={trackType}>
        {children}
      </TrackPositioner>
    </TrackContext.Provider>
  )
}

export { TrackPositionerWithContext as TrackPositioner, useTrackContext }
